import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

export default function AccountingOpsReconciliationPage() {
  const { hospitalId } = useAccountingHospitalContext();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["accounting-ops-recon", hospitalId, fromDate, toDate],
    queryFn: () =>
      accountingApi.opsReconciliation(hospitalId, {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      }),
    enabled: !!hospitalId,
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">Ops vs books reconciliation</h2>
        <p className="mt-1 text-sm text-slate-500">
          Compares operational totals to posted accounting vouchers by event type. Historical
          ops will not match until a migration is approved; this is for going-forward checks.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-44 space-y-1">
          <Label className="text-xs">From (voucher / ops date)</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-44 space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {data?.note ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {data.note}
        </p>
      ) : null}

      {isLoading || isFetching ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line</TableHead>
                <TableHead className="text-right">Ops</TableHead>
                <TableHead className="text-right">Books</TableHead>
                <TableHead className="text-right">Difference</TableHead>
                <TableHead className="text-right">Vouchers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.lines ?? []).map((line) => (
                <TableRow key={line.eventType}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{line.label}</div>
                    <div className="text-xs text-slate-400">{line.eventType}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{formatInr(line.ops)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatInr(line.books)}</TableCell>
                  <TableCell
                    className={`text-right tabular-nums ${
                      Math.abs(line.difference) > 0.009 ? "text-amber-700" : "text-slate-600"
                    }`}
                  >
                    {formatInr(line.difference)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{line.voucherCount}</TableCell>
                </TableRow>
              ))}
              {data?.totals ? (
                <TableRow className="bg-slate-50 font-medium">
                  <TableCell>Totals (sum of lines)</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(data.totals.ops)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(data.totals.books)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatInr(data.totals.difference)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
